import { floorPlan } from './interior';
import type { Registry } from '../content/registry';
import type { Place, Prop, Road, RoadClass, Terrain, ZoneName } from '../content/types';
import { joinBlocks, placeBuilding, placeWings, vacantLot, type PlacedBuilding } from './buildings';
import { Fields } from './fields';
import { LotPlanner, type Lot } from './lots';
import { hash, hash01 } from './rng';
import { Relief, TERRACE } from './relief';
import { BANK, BANK_TOE, OVER_H, RoadNet, type RoadHit } from './roads';
import { CELL, PathNet } from './paths';
import { PathRoads, type RoadSource } from './path-adapter';
import { PathLots } from './path-lots';
import { PlaceIndex } from './places';
import { buildingHeight } from '../content/types';
import { driveway, isBusiness, type Driveway } from './parking';
import { boardPosts, boardTiles, type PlacedBoard } from './boards';

export const CHUNK = 64;
/** (Sep 23) the path world's lamps: the spacing along a road (tiles, the two sides in turn), the density a road must reach to be lit, how far from a junction's middle they start; and one bigger junction in how many gets a Phlock camera */
const LAMP_EVERY = 12, LIT_DENSITY = 0.12, JUNCTION_CLEAR = 11, CAM_JUNCTIONS = 7;
/** (owner, Sep 25) a big billboard stands this far from a road's painted band (tiles, to the face's near edge) and this far from a junction's middle */
const BOARD_GAP = 3, BOARD_CLEAR = 24;
/** (track 2) the ground's height is sampled every this many tiles and interpolated between (see `WorldGen.ground`) */
export const GROUND_STEP = 4;
export const ZONES: ZoneName[] = ['water', 'rural', 'suburb', 'industrial', 'downtown', 'park'];
export const ZONE_INDEX: Record<ZoneName, number> = { water: 0, rural: 1, suburb: 2, industrial: 3, downtown: 4, park: 5 };

export const RF_V = 1, RF_H = 2, RF_VC = 4, RF_HC = 8, RF_SIDEWALK = 16, RF_VEVEN = 32, RF_HEVEN = 64, /** deck tiles only: a ledge (see RoadHit.ledge in roads.ts) */ RF_LEDGE = 128;

export interface Decor { x: number; y: number; terrain: number; index: number }
/** the platform strip at a railway's dead end: this many tiles back from the end (owner, Q66) */
export const PLATFORM_LEN = 24;
/** how far (in tiles, 0 = the last) a rail-side tile is from the dead end of its track along the track's axis, when within PLATFORM_LEN; the
 *  ballast (|off| ≤ 1) ends where the rail line does, so the tile's own coordinate along the axis is walked to the end */
export function railEndNear(net: RoadSource, x: number, y: number): number | undefined {
  for (const [dx, dy] of [[2, 0], [-2, 0], [0, 2], [0, -2]] as const) {
    const axis = dx ? 'v' : 'h', cx = x + dx, cy = y + dy;
    const rail = net.hitsAt(cx, cy).find((h) => h.axis === axis && h.road.class === 'rail' && h.off === 0); if (!rail) continue;
    const isRail = (u: number): boolean => net.hitsAt(axis === 'v' ? cx : u, axis === 'v' ? u : cy).some((h) => h.axis === axis && h.road.class === 'rail' && h.pos === rail.pos);
    const u0 = axis === 'v' ? cy : cx;
    for (const dir of [1, -1] as const) for (let d = 1; d <= PLATFORM_LEN; d++) if (!isRail(u0 + dir * d)) return d - 1;
  }
  return undefined;
}
/** (track 2) how a world is generated: `paths` runs it on the path network (mapgen) instead of the lattice — a different world for the same seed */
export interface WorldGenOptions { paths?: boolean; /** (owner, Sep 24) the path network smooths its roads through the nodes (paths.ts) — a different world for the same seed */ smooth?: boolean }
export interface PlacedProp { x: number; y: number; prop: number /* index into gen.propList */ }

export interface Chunk {
  cx: number; cy: number;
  /** tile origin */
  x0: number; y0: number;
  terrain: Uint16Array;
  road: Uint16Array;
  roadFlags: Uint8Array;
  zone: Uint8Array;
  /** (track 2) the ground's height, metres, smooth (the raster terraces it; the physics reads its gradient) */
  height: Float32Array;
  /** (track 2) how lit the ground is from the north-west, −64..64 around 0 = flat — what makes a slope read */
  lit: Int8Array;
  /** (track 2) a terrace step on this tile's east (1) and/or south (2) edge — the line a bench is drawn with */
  step: Uint8Array;
  /** blocking height in metres per tile (buildings + decor); 0 = free */
  blockH: Float32Array;
  /** height of decor only (trees, rocks) — aircraft roll over these on the ground */
  decorH: Float32Array;
  /** overpass deck layer: height (0 = no deck), road index + 1 and flags of the road on the deck */
  deckH: Float32Array;
  deckRoad: Uint16Array;
  deckFlags: Uint8Array;
  buildings: PlacedBuilding[];
  /** construction sites: buildings on player-zoned lots a crew has not finished yet (overlay.ts `sites` has the progress) */
  sites: PlacedBuilding[];
  /** parking lots beside businesses (paved, no decor; NPC cars pull in — owner's answer Q36), keyed by building */
  parking: (Driveway & { key: string })[];
  lots: Lot[];
  decor: Decor[];
  props: PlacedProp[];
  /** (owner, Sep 25) the big billboards whose centre is in this chunk (world/boards.ts); their posts' blockH is set by every chunk they stand in */
  boards?: PlacedBoard[];
}

export class WorldGen {
  readonly fields: Fields;
  /** (track 2) the ground's height — the chunk carries it, the raster shades by it, the physics feels it */
  readonly relief: Relief;
  /** the road network the chunks are cut from: the lattice, or (`paths`) the path network through its adapter */
  readonly net: RoadSource;
  /** the lattice, always: the player-built pieces (overlay.ts) are pinned to it whichever network runs underneath */
  readonly lattice: RoadNet;
  /** (track 2, the network swap) the path network, when this world runs on it — docs/MAPGEN_DECISION.md */
  readonly paths?: PathNet;
  readonly lots: { lotsIn(x0: number, y0: number, x1: number, y1: number): Lot[] };
  readonly places: PlaceIndex;
  readonly terrainList: Terrain[];
  readonly roadList: Road[];
  readonly propList: Prop[];
  private propsByPlacement: Record<string, { prop: Prop; index: number }[]> = {};
  private terrainIndex = new Map<string, number>();
  private roadIndex = new Map<string, number>();
  /** the road def slip lanes are painted with */
  private frontageRoad?: Road;

  constructor(readonly seed: number, readonly reg: Registry, opts: WorldGenOptions = {}) {
    this.fields = new Fields(seed, reg);
    this.relief = new Relief(this.fields, seed);
    this.lattice = new RoadNet(this.fields);
    if (opts.paths) {
      // the swap: the same generator, the roads and the lots from the path network — everything downstream reads `net`
      this.paths = new PathNet(this.fields, seed, this.relief, { smooth: !!opts.smooth });
      const roads = new PathRoads(this.paths, reg);
      this.net = roads;
      this.lots = new PathLots(this.fields, roads, this.lattice);
    } else {
      this.net = this.lattice;
      this.lots = new LotPlanner(this.fields, this.lattice);
    }
    this.places = new PlaceIndex(reg, seed, this.fields, this.net);
    this.terrainList = reg.all('terrain');
    this.terrainList.forEach((t, i) => this.terrainIndex.set(t.id, i));
    this.roadList = reg.all('road');
    this.roadList.forEach((r, i) => this.roadIndex.set(r.id, i));
    this.frontageRoad = this.roadList.find((r) => r.class === 'street') ?? this.roadList.find((r) => r.class === 'avenue');
    this.propList = reg.all('prop');
    this.propList.forEach((p, index) => { (this.propsByPlacement[p.placement] ??= []).push({ prop: p, index }); });
    for (const z of reg.all('zone')) if (z.groundTerrain) this.zoneGround[ZONE_INDEX[z.id]] = this.terrainIndex.get(z.groundTerrain);
  }
  private zoneGround: (number | undefined)[] = [];

  /** (path world) is this tile under a road's painted band — the stroke, `slack` tiles either side (see `PathRoads.strokeOff`) */
  private underStroke(x: number, y: number, slack: number): boolean {
    if (!this.paths) return false;
    const so = (this.net as PathRoads).strokeOff(x, y, slack);
    return !!so && so.off <= so.total / 2 + slack;
  }
  /**
   * (Sep 17, measured: a lone car U-turned halfway along a straight avenue) does a place keep this road? `keep`: all of them.
   * `major`: the coarse network — level 0 on the lattice (the 128-spacing lines); on the path world the through roads, level 0
   * and 1 (the 512- and 256-step edges between towns: highways, avenues, boulevards, the railway). `none`: the rest dropped —
   * but on the path world a through road is never cut, whatever the place says: the farm estate at 200,200 (authored for the
   * lattice, where the next line is a block over) sat on the avenue between two towns and painted farmland over eighty tiles of
   * it, while the drivers' map (the network's `hitsAt`) still led them in; the road the tiles show and the road the map shows
   * have to be the same road.
   */
  private roadKept(mode: 'keep' | 'major' | 'none', h: RoadHit): boolean {
    if (mode === 'keep') return true;
    const through = this.paths ? h.level <= 1 : h.level === 0;
    return through && (mode === 'major' || !!this.paths);
  }
  /**
   * (owner, Sep 23: "street lights should show up way more often, so people see where they walk and drive at night"; the Phlock cameras
   * rare — "earlier there were too many" — but at the bigger junctions) The path world's street furniture, which the tile pass above
   * cannot give it: its pavements are the strokes' own, never flagged as sidewalk, so no lamp or camera was ever placed. A lamp every
   * LAMP_EVERY tiles along each street, avenue and motorway in a town's zones (and a built-up place's lanes), the two sides in turn, in the middle
   * of the pavement, clear of the junction boxes; a camera on a corner of one bigger junction in CAM_JUNCTIONS. Whoever holds the tile
   * places it (the edges come from the 3 × 3 cells around, so a lamp near the chunk's edge is not lost), deterministic as everything is.
   */
  private pathFurniture(c: Chunk, x0: number, y0: number, doorsteps: Set<number>, noProp: Set<number>): void {
    const pr = this.net as PathRoads, net = pr.net;
    const idOf = (id: string) => this.propList.findIndex((q) => q.id === id);
    const lamp = idOf('lamp_post'), double = idOf('streetlight_double'), camera = idOf('traffic_camera');
    const taken = new Set<number>(c.props.map((q) => (q.y - y0) * CHUNK + (q.x - x0)));
    const place = (x: number, y: number, prop: number, town = false): boolean => {
      const tx = Math.floor(x), ty = Math.floor(y), lx = tx - x0, ly = ty - y0;
      if (prop < 0 || lx < 0 || ly < 0 || lx >= CHUNK || ly >= CHUNK) return false;
      const i = ly * CHUNK + lx;
      if (taken.has(i) || c.blockH[i] > 0 || c.deckH[i] > 0 || c.zone[i] === ZONE_INDEX.water || doorsteps.has(i) || noProp.has(i)) return false;
      if (town && c.zone[i] === ZONE_INDEX.rural) return false; // (a lamp is a town's: the country's roads stay dark)
      const so = pr.strokeOff(tx, ty, 1); if (!so || !(so.off > so.width / 2 + 0.4)) return false; // (on the pavement or its verge, never the carriageway — whole tiles: strokeOff reads its table by the tile)
      const def = this.propList[prop];
      c.props.push({ x: tx, y: ty, prop }); taken.add(i);
      if (def.height > 0) { c.blockH[i] = Math.max(c.blockH[i], def.height); c.decorH[i] = Math.max(c.decorH[i], def.height); }
      return true;
    };
    const ccx = Math.floor(x0 / CELL), ccy = Math.floor(y0 / CELL), seen = new Set<string>();
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) for (const e of net.bucket(ccx + dx, ccy + dy)) {
      if (seen.has(e.key)) continue; seen.add(e.key);
      const dens = Math.max(e.a.density, e.b.density);
      if (e.cls === 'rail' || e.cls === 'dirt' || (e.cls === 'lane' && dens < LIT_DENSITY)) continue; // (every town road is lit — the zone under each lamp decides; a lane only where it is built up)
      const pts = e.pts, n = pts.length / 2; let len = 0; for (let k = 1; k < n; k++) len += Math.hypot(pts[k * 2] - pts[k * 2 - 2], pts[k * 2 + 1] - pts[k * 2 - 1]);
      let next = JUNCTION_CLEAR + hash01(this.seed + 811, e.a.cx * 3 + e.b.cx, e.a.cy * 3 + e.b.cy) * LAMP_EVERY * 0.5, along = 0, side = hash01(this.seed + 813, e.a.cx, e.a.cy) < 0.5 ? 1 : -1;
      for (let k = 1; k < n && next <= len - JUNCTION_CLEAR; k++) {
        const ax = pts[k * 2 - 2], ay = pts[k * 2 - 1], bx = pts[k * 2], by = pts[k * 2 + 1], seg = Math.hypot(bx - ax, by - ay) || 1e-6;
        while (next <= along + seg && next <= len - JUNCTION_CLEAR) {
          const t = (next - along) / seg, px = ax + (bx - ax) * t, py = ay + (by - ay) * t, nx = -(by - ay) / seg, ny = (bx - ax) / seg;
          const so = pr.strokeOff(Math.round(px), Math.round(py), 1), halfW = so ? so.width / 2 : 3, halfT = so ? so.total / 2 : halfW + 1.5;
          const off = halfT > halfW + 1 ? (halfW + halfT) / 2 : halfW + 1.2;
          const prop = e.cls === 'avenue' && dens > 0.3 && double >= 0 ? double : lamp;
          if (!place(px + nx * off * side, py + ny * off * side, prop, true)) place(px - nx * off * side, py - ny * off * side, prop, true); // (the other side when this one is a doorstep or a wall)
          side = -side; next += LAMP_EVERY;
        }
        along += seg;
      }
    }
    // the cameras: a corner of a bigger junction (three roads or more, one an avenue or a motorway), one in CAM_JUNCTIONS
    if (camera >= 0) for (const j of net.junctionsIn(x0, y0, x0 + CHUNK, y0 + CHUNK)) {
      if (j.x < x0 || j.x >= x0 + CHUNK || j.y < y0 || j.y >= y0 + CHUNK || j.arms.length < 3) continue;
      if (!j.arms.some((a) => a.edge.cls === 'avenue' || a.edge.cls === 'highway') || hash01(this.seed + 817, j.cx, j.cy) >= 1 / CAM_JUNCTIONS) continue;
      const a0 = hash01(this.seed + 819, j.cx, j.cy) * Math.PI * 2;
      done: for (const r of [7, 9, 11, 13]) for (let q = 0; q < 8; q++) { const a = a0 + (q * Math.PI) / 4; if (place(j.x + Math.cos(a) * r, j.y + Math.sin(a) * r, camera)) break done; }
    }
  }
  /**
   * (owner, Sep 25: "add bigger billboards that could have logos or images") The big roadside boards (content/props, placement `board`,
   * `on: road`): one spot every `every` tiles along each road of the board's classes, BOARD_CLEAR from the junctions; a spot gets a board
   * by density × the zone weight, on the side a hash picks (the other when that one is taken), parallel to the road, BOARD_GAP beyond
   * its painted band. Only on free ground — no lot, no place, no road, no water under the face or a tile round it — decided from the
   * network and the lot planner alone (never this chunk's arrays), so every chunk that sees the board agrees it is there. The chunk
   * holding the centre lists it; each chunk sets blockH on the posts that stand in it and clears the decor under the face.
   */
  private pathBoards(c: Chunk, x0: number, y0: number): void {
    const specs = (this.propsByPlacement.board ?? []).filter((q) => q.prop.board?.on === 'road');
    if (!specs.length) return;
    const pr = this.net as PathRoads, net = pr.net, hits: RoadHit[] = [];
    // on a lot, the face may hang over the yard, the forecourt or a bare plot (a billboard over a car lot or a vacant plot is a strip's
    // oldest sight) but never over the building, a tile round it; the posts stand clear of its door's two tiles and its driveway pads.
    // A lot somebody bought, designed or zoned is theirs whole. All from the lot planner and `placeBuilding`: the same in every chunk
    type Rect = [number, number, number, number];
    const ov = this.fields.overlay, keepOut = new Map<string, { face: Rect[]; posts: Rect[] }>();
    const lotRects = (l: Lot): { face: Rect[]; posts: Rect[] } => {
      let r = keepOut.get(l.key); if (r) return r;
      if (l.planned || ov.plots.has(l.key) || ov.customs.has(l.key)) r = { face: [[l.x, l.y, l.w, l.h]], posts: [] };
      else {
        const b = placeBuilding(this.reg, this.fields, l);
        r = b ? { face: [[b.x - 1, b.y - 1, b.w + 2, b.h + 2]], posts: [[b.x - 2, b.y - 2, b.w + 4, b.h + 4], ...[driveway(b, this.seed), driveway(b, this.seed, true)].map((d): Rect => [d.x - 1, d.y - 1, d.w + 2, d.h + 2])] } : { face: [], posts: [] };
      }
      keepOut.set(l.key, r); return r;
    };
    const inRect = (x: number, y: number, [rx, ry, rw, rh]: Rect) => x >= rx && x < rx + rw && y >= ry && y < ry + rh;
    const clear = (b: PlacedBoard): boolean => {
      const tiles = boardTiles(b, 1);
      let lx0 = Infinity, ly0 = Infinity, lx1 = -Infinity, ly1 = -Infinity; for (const [x, y] of tiles) { lx0 = Math.min(lx0, x); ly0 = Math.min(ly0, y); lx1 = Math.max(lx1, x); ly1 = Math.max(ly1, y); }
      const near = this.lots.lotsIn(lx0 - 8, ly0 - 8, lx1 + 9, ly1 + 9).map(lotRects), face = near.flatMap((r) => r.face), posts = near.flatMap((r) => r.posts);
      for (const [x, y] of tiles) {
        const t = this.fields.terrainAt(x, y); if (!t.buildable || !t.walkable) return false;
        if (this.underStroke(x, y, 1) || this.places.at(x, y)) return false;
        if (this.net.hitsAt(x, y, hits).some((h) => RoadNet.covers(0, h.road.width + 2 * (h.road.sidewalk ?? 0) + 2, h.off))) return false; // (the rails too: the stroke leaves them out)
        if (face.some((r) => inRect(x, y, r))) return false;
      }
      for (const [px, py] of boardPosts(b)) if (posts.some((r) => inRect(Math.floor(px), Math.floor(py), r))) return false;
      return true;
    };
    const ccx = Math.floor(x0 / CELL), ccy = Math.floor(y0 / CELL), seen = new Set<string>();
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) for (const e of net.bucket(ccx + dx, ccy + dy)) {
      if (seen.has(e.key)) continue; seen.add(e.key);
      for (const { prop, index } of specs) {
        const bs = prop.board!; if (!bs.roads?.includes(e.cls as never)) continue;
        const every = bs.every ?? 80, pts = e.pts, n = pts.length / 2, cum = [0];
        for (let k = 1; k < n; k++) cum.push(cum[k - 1] + Math.hypot(pts[k * 2] - pts[k * 2 - 2], pts[k * 2 + 1] - pts[k * 2 - 1]));
        const len = cum[n - 1], clearEnd = BOARD_CLEAR + bs.w / 2;
        /** the point `u` tiles along the edge, and the unit direction there */
        const at = (u: number): [number, number, number, number] => { let k = 1; while (k < n - 1 && cum[k] < u) k++; const seg = cum[k] - cum[k - 1] || 1e-6, t = Math.max(0, Math.min(1, (u - cum[k - 1]) / seg)), ax = pts[k * 2 - 2], ay = pts[k * 2 - 1], bx = pts[k * 2], by = pts[k * 2 + 1]; return [ax + (bx - ax) * t, ay + (by - ay) * t, (bx - ax) / seg, (by - ay) / seg]; };
        for (let u0 = clearEnd + hash01(this.seed + 821 + index, e.a.cx * 3 + e.b.cx, e.a.cy * 3 + e.b.cy) * every; u0 <= len - clearEnd; u0 += every) {
          const [sx, sy] = at(u0), r = hash01(this.seed + 823 + index, Math.round(sx), Math.round(sy));
          if (r >= prop.density * (prop.zones[this.fields.urbanZone(sx, sy)] ?? 0)) continue;
          const side = hash01(this.seed + 825 + index, Math.round(sx), Math.round(sy)) < 0.5 ? 1 : -1;
          // the spot, or a little along the road either way, this side or the other, at the kerb's distance or a little back — the first that is free
          let board: PlacedBoard | undefined;
          search: for (const slide of [0, -12, 12, -24, 24]) {
            const u = u0 + slide; if (u < clearEnd || u > len - clearEnd) continue;
            const [px, py, dx, dy] = at(u), so = pr.strokeOff(Math.round(px), Math.round(py), 1), halfT = so ? so.total / 2 : 4;
            let a = Math.atan2(dy, dx); if (a > Math.PI / 2) a -= Math.PI; else if (a <= -Math.PI / 2) a += Math.PI; // (the face reads left to right whichever way the edge was drawn)
            for (const sd of [side, -side]) for (const gap of [BOARD_GAP, BOARD_GAP + 3]) {
              const off = halfT + gap + bs.d / 2, b: PlacedBoard = { x: px - dy * off * sd, y: py + dx * off * sd, a, w: bs.w, d: bs.d, lift: bs.lift, base: 0, prop: index, road: e.cls as RoadClass };
              if (clear(b)) { board = b; break search; }
            }
          }
          if (!board) continue;
          if (Math.floor(board.x / CHUNK) === c.cx && Math.floor(board.y / CHUNK) === c.cy) (c.boards ??= []).push(board);
          // the decor and tall props under the face go; the posts block (a car stops at a steel post, a walker passes under the face)
          const under = new Set<number>(); for (const [tx, ty] of boardTiles(board)) { const lx = tx - x0, ly = ty - y0; if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) under.add(ly * CHUNK + lx); }
          if (!under.size) continue;
          c.decor = c.decor.filter((d) => { const i = (d.y - y0) * CHUNK + (d.x - x0); if (!under.has(i)) return true; if (c.blockH[i] <= c.decorH[i] + 1e-6) c.blockH[i] = 0; c.decorH[i] = 0; return false; });
          c.props = c.props.filter((q) => { const i = (q.y - y0) * CHUNK + (q.x - x0); if (!under.has(i) || this.propList[q.prop].height <= 0) return true; if (c.blockH[i] <= c.decorH[i] + 1e-6) c.blockH[i] = 0; c.decorH[i] = 0; return false; });
          for (const [qx, qy] of boardPosts(board)) { const lx = Math.floor(qx) - x0, ly = Math.floor(qy) - y0; if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) { const i = ly * CHUNK + lx; c.blockH[i] = Math.max(c.blockH[i], bs.lift); } }
        }
      }
    }
  }
  /**
   * (owner, Sep 25) The rooftop boards (placement `board`, `on: roof`): a flat roof downtown, the building `minHeight` tall and
   * `minLength` long, gets a face along its long side `lift` metres over the roof — density × the zone weight per building, by its
   * position. The chunk holding the building's origin lists it (the one that draws the building).
   */
  private roofBoards(c: Chunk): void {
    const specs = (this.propsByPlacement.board ?? []).filter((q) => q.prop.board?.on === 'roof');
    if (!specs.length) return;
    for (const b of c.buildings) {
      if (Math.floor(b.x / CHUNK) !== c.cx || Math.floor(b.y / CHUNK) !== c.cy || b.ruined || b.main || (b.def.roofForm && b.def.roofForm.type !== 'flat') || b.def.tags.includes('billboard')) continue;
      const long = Math.max(b.w, b.h), zone = this.fields.urbanZone(b.x + b.w / 2, b.y + b.h / 2);
      for (const { prop, index } of specs) {
        const bs = prop.board!;
        if (b.height < (bs.minHeight ?? 12) || long < (bs.minLength ?? bs.w + 1) || Math.min(b.w, b.h) < bs.d + 1) continue;
        if (hash01(this.seed + 831 + index, b.x, b.y) >= prop.density * (prop.zones[zone] ?? 0)) continue;
        (c.boards ??= []).push({ x: b.x + b.w / 2, y: b.y + b.h / 2, a: b.w >= b.h ? 0 : Math.PI / 2, w: Math.min(bs.w, long - 1), d: bs.d, lift: bs.lift, base: b.height, prop: index, roof: b.key });
        break;
      }
    }
  }
  private nearRoad(c: Chunk, lx: number, ly: number): boolean {
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) { const nx = lx + dx, ny = ly + dy; if (nx < 0 || ny < 0 || nx >= CHUNK || ny >= CHUNK) continue; if (c.road[ny * CHUNK + nx]) return true; }
    return false;
  }

  /** a finer road exists at this tile only when its centre line is on land (the same rule the tile loop uses) */
  private shoreOk(h: RoadHit, x: number, y: number): boolean { const ct = h.axis === 'v' ? this.fields.terrainAt(h.pos, y) : this.fields.terrainAt(x, h.pos); return ct.walkable || ct.buildable; }
  /**
   * The ground's height for a chunk, sampled on a coarse grid (`GROUND_STEP` tiles) and interpolated — a relief sample
   * costs several noise octaves and a chunk has 4096 tiles, so per tile it would be the whole chunk budget again. An
   * apron of one sample round the chunk keeps the gradient and the terrace steps continuous across chunk borders.
   * Then per tile: the smooth height, how lit it is (the slope against a light from the north-west), and whether a
   * terrace bench ends on its east or south edge.
   */
  /**
   * (track 2) A building stands on a pad: the ground under its footprint is set to one height — the relief at its centre,
   * sampled unblurred so a footprint straddling a chunk border gets the same pad from both sides — and the light and the
   * steps are redone round it, so a house never sits across a terrace step. The world's cut-and-fill, one rectangle at a time.
   */
  /**
   * (owner, Sep 25: Area 69) a place with `level` stands on levelled ground: inside its rect the relief is pulled to the rect's mean
   * height, fully `level` tiles in from the edge and smoothstepped to nothing at it, so the ground outside is untouched. Only the
   * chunk's heights see it — the road network prices the land as it lies, which is what placed the site — and it is a pure function
   * of the tile, so two chunks agree across their border.
   */
  private levelledIn(c: Chunk): Place[] {
    const P = CHUNK * 0.25;
    return this.places.in(c.x0 - P, c.y0 - P, c.x0 + CHUNK + P, c.y0 + CHUNK + P).filter((p) => !!p.level);
  }
  private levels = new Map<string, number>();
  private groundAt(x: number, y: number, flat: readonly Place[]): number {
    let h = this.relief.smooth(x, y, true);
    for (const p of flat) {
      if (x <= p.x || y <= p.y || x >= p.x + p.w || y >= p.y + p.h) continue;
      const key = `${p.id}:${p.x},${p.y}`;
      let lv = this.levels.get(key);
      if (lv === undefined) { lv = 0; for (let j = 0; j <= 8; j++) for (let i = 0; i <= 8; i++) lv += this.relief.smooth(p.x + (p.w * i) / 8, p.y + (p.h * j) / 8, true) / 81; if (this.levels.size > 256) this.levels.clear(); this.levels.set(key, lv); }
      const t = Math.min(1, Math.min(x - p.x, y - p.y, p.x + p.w - x, p.y + p.h - y) / p.level!), w = t * t * (3 - 2 * t);
      h += (lv - h) * w;
    }
    return h;
  }
  private pads(c: Chunk): void {
    if (!c.buildings.length && !c.sites.length) return;
    const G = GROUND_STEP, m = CHUNK / G + 3, flat = this.levelledIn(c);
    const terrace = (h: number) => Math.round(h / TERRACE) * TERRACE;
    let x0 = CHUNK, y0 = CHUNK, x1 = -1, y1 = -1;
    for (const b of [...c.buildings, ...c.sites]) {
      // (bug report, Sep 12: "this area renders weird, like a box on top of the grass") a flat lot — a field, a club's strip, a
      // park — is ground with a picture on it, not a slab: levelling it drew the relief's slope round a 12-tile plateau
      if ('height' in b && b.height <= 0) continue;
      const pad = this.groundAt(b.x + b.w / 2, b.y + b.h / 2, flat);
      const lx0 = Math.max(0, b.x - c.x0), ly0 = Math.max(0, b.y - c.y0), lx1 = Math.min(CHUNK, b.x + b.w - c.x0), ly1 = Math.min(CHUNK, b.y + b.h - c.y0);
      if (lx1 <= lx0 || ly1 <= ly0) continue;
      for (let ly = ly0; ly < ly1; ly++) for (let lx = lx0; lx < lx1; lx++) c.height[ly * CHUNK + lx] = pad;
      x0 = Math.min(x0, lx0 - 2); y0 = Math.min(y0, ly0 - 2); x1 = Math.max(x1, lx1 + 2); y1 = Math.max(y1, ly1 + 2);
    }
    if (x1 < 0) return;
    void m;
    // the light and the steps again round the pads, from the chunk's own heights (the border tiles keep their relief neighbours)
    const hAt = (lx: number, ly: number): number => c.height[Math.min(CHUNK - 1, Math.max(0, ly)) * CHUNK + Math.min(CHUNK - 1, Math.max(0, lx))];
    for (let ly = Math.max(0, y0); ly < Math.min(CHUNK, y1); ly++) for (let lx = Math.max(0, x0); lx < Math.min(CHUNK, x1); lx++) {
      const i = ly * CHUNK + lx;
      const gx = (hAt(lx + 2, ly) - hAt(lx - 2, ly)) / 4, gy = (hAt(lx, ly + 2) - hAt(lx, ly - 2)) / 4;
      c.lit[i] = Math.max(-64, Math.min(64, Math.round(-(gx + gy) * 0.9 * 1.6 * 64)));
      const t = terrace(c.height[i]);
      c.step[i] = (lx + 1 < CHUNK && terrace(hAt(lx + 1, ly)) !== t ? 1 : 0) | (ly + 1 < CHUNK && terrace(hAt(lx, ly + 1)) !== t ? 2 : 0);
    }
  }
  private ground(c: Chunk): void {
    const G = GROUND_STEP, m = CHUNK / G + 3; // samples: one apron each side
    const smp = new Float32Array(m * m), flat = this.levelledIn(c);
    for (let j = 0; j < m; j++) for (let i = 0; i < m; i++) smp[j * m + i] = this.groundAt(c.x0 + (i - 1) * G, c.y0 + (j - 1) * G, flat); // (the unblurred sample: over four tiles the blur is invisible — track 1's finding)
    const at = (i: number, j: number) => smp[j * m + i];
    const sample = (x: number, y: number): number => { // bilinear, in sample units (x = 0 at the chunk's first tile)
      const u = x / G + 1, v = y / G + 1, i = Math.floor(u), j = Math.floor(v), fu = u - i, fv = v - j;
      const i1 = Math.min(m - 1, i + 1), j1 = Math.min(m - 1, j + 1);
      return (at(i, j) * (1 - fu) + at(i1, j) * fu) * (1 - fv) + (at(i, j1) * (1 - fu) + at(i1, j1) * fu) * fv;
    };
    const terrace = (h: number) => Math.round(h / TERRACE) * TERRACE;
    for (let ly = 0; ly < CHUNK; ly++) {
      for (let lx = 0; lx < CHUNK; lx++) {
        const i = ly * CHUNK + lx, h = sample(lx + 0.5, ly + 0.5);
        c.height[i] = h;
        const gx = (sample(lx + 2.5, ly + 0.5) - sample(lx - 1.5, ly + 0.5)) / 4, gy = (sample(lx + 0.5, ly + 2.5) - sample(lx + 0.5, ly - 1.5)) / 4;
        c.lit[i] = Math.max(-64, Math.min(64, Math.round(-(gx + gy) * 0.9 * 1.6 * 64)));
        const t = terrace(h);
        c.step[i] = (terrace(sample(lx + 1.5, ly + 0.5)) !== t ? 1 : 0) | (terrace(sample(lx + 0.5, ly + 1.5)) !== t ? 2 : 0);
      }
    }
  }
  terrainOf(c: Chunk, lx: number, ly: number): Terrain { return this.terrainList[c.terrain[ly * CHUNK + lx]]; }
  roadOf(c: Chunk, lx: number, ly: number): Road | undefined { const i = c.road[ly * CHUNK + lx]; return i ? this.roadList[i - 1] : undefined; }

  /** water under any corner or the centre of a footprint (the fields' terrain: a place's own paint does not count) */
  private wetUnder(x: number, y: number, w: number, h: number): boolean {
    for (const [px, py] of [[x, y], [x + w - 1, y], [x, y + h - 1], [x + w - 1, y + h - 1], [x + Math.floor(w / 2), y + Math.floor(h / 2)]]) { const t = this.fields.terrainAt(px, py); if (!t.walkable && !t.buildable) return true; }
    return false;
  }
  generate(cx: number, cy: number): Chunk {
    const n = CHUNK * CHUNK;
    const x0 = cx * CHUNK, y0 = cy * CHUNK;
    const c: Chunk = {
      cx, cy, x0, y0,
      terrain: new Uint16Array(n), road: new Uint16Array(n), roadFlags: new Uint8Array(n),
      zone: new Uint8Array(n), height: new Float32Array(n), lit: new Int8Array(n), step: new Uint8Array(n), blockH: new Float32Array(n), decorH: new Float32Array(n), deckH: new Float32Array(n), deckRoad: new Uint16Array(n), deckFlags: new Uint8Array(n), buildings: [], sites: [], parking: [], lots: [], decor: [], props: [], boards: [],
    };
    this.ground(c);
    const ov = this.fields.overlay;
    const hits: RoadHit[] = [];
    const inLot = new Uint8Array(n);

    const places = this.places.in(x0, y0, x0 + CHUNK, y0 + CHUNK);
    const blockedByPlace = (x: number, y: number, w: number, h: number) => places.some((p) => (p.lots ?? 'none') === 'none' && x < p.x + p.w && x + w > p.x && y < p.y + p.h && y + h > p.y);

    // lots + buildings first (they repaint ground)
    // lots a little beyond the chunk too: a neighbour's business may have its parking lot on our side of the border
    const PAD = 8;
    const lotsNear = this.lots.lotsIn(x0 - PAD, y0 - PAD, x0 + CHUNK + PAD, y0 + CHUNK + PAD).filter((l) => !blockedByPlace(l.x, l.y, l.w, l.h));
    const inChunk = (l: { x: number; y: number; w: number; h: number }) => l.x < x0 + CHUNK && l.x + l.w > x0 && l.y < y0 + CHUNK && l.y + l.h > y0;
    c.lots = lotsNear.filter(inChunk);
    // a building whose footprint a road runs through (a parallel line the lot planner did not see — the owner's "buildings on roads")
    // is not built; decided from the global road function so every chunk that lists the building agrees
    const roadHits: RoadHit[] = [];
    const onRoad = (b: { x: number; y: number; w: number; h: number }): boolean => {
      for (let y = b.y; y < b.y + b.h; y++) for (let x = b.x; x < b.x + b.w; x++) {
        const mode = (places.length ? this.places.at(x, y) : undefined)?.roads ?? 'keep';
        for (const h of this.net.hitsAt(x, y, roadHits)) { if (!this.roadKept(mode, h)) continue; if (this.net.overpassH(h, x, y) > 0) continue; if (RoadNet.covers(0, h.road.width + 2, h.off)) return true; }
      }
      return false;
    };
    const nearBuildings: PlacedBuilding[] = [];
    for (const p of places) {
      for (const [i, pb] of (p.buildings ?? []).entries()) {
        const def = this.reg.get('building', pb.id);
        if (!def) continue;
        const rot = (pb.rot ?? 0) as 0 | 1 | 2 | 3;
        const [fw, fd] = def.footprint;
        const w = rot % 2 ? fd : fw, h = rot % 2 ? fw : fd;
        const bx = p.x + pb.x, by = p.y + pb.y;
        if (bx >= x0 + CHUNK || bx + w <= x0 || by >= y0 + CHUNK || by + h <= y0) continue;
        const pb2: PlacedBuilding = { key: `place:${p.id}:${i}`, id: def.id, def, x: bx, y: by, w, h, rot, height: buildingHeight(def) };
        if (onRoad(pb2)) continue; // (a place's layout laid over a through road: that piece is left out)
        if (!p.terrain && !(p.paint ?? []).some((r) => bx + w / 2 >= p.x + r.x && bx + w / 2 < p.x + r.x + r.w && by + h / 2 >= p.y + r.y && by + h / 2 < p.y + r.y + r.h) && this.wetUnder(bx, by, w, h)) continue; // (a place that paints its own ground stands on that, not on the lake) // (a place is content at fixed coordinates: on a seed it was not drawn for, a piece of it can land in a lake — owner, Sep 11: "a few buildings on water")
        c.buildings.push(pb2);
      }
    }
    for (const lot of lotsNear) {
      const b0 = placeBuilding(this.reg, this.fields, lot);
      const b = b0 && onRoad(b0) ? undefined : b0;
      const here = inChunk(lot);
      // (owner, Sep 22) a bare lot on buildable ground is a plot for sale (world/custom.ts): the generator left it, nothing fit, or its owner has not designed yet.
      // (the lot object may be the planner's cached one, shared by every chunk that sees it — set either way, so a design placed later clears the flag)
      if (!b && !onRoad(lot) && vacantLot(this.fields, lot)) lot.vacant = true; else delete lot.vacant;
      if (lot.planned || (b && ov.customs.has(lot.key))) {
        // a player-zoned lot (or a design on a bought plot): a site until a construction crew delivered everything; untouched ground until they start
        if (!b) continue;
        const progress = ov.progress(lot.key);
        const custom = ov.customs.get(lot.key), blocks = custom?.wings?.length ? [b, ...placeWings(this.reg, this.fields, lot, custom, b).filter((wg) => !onRoad(wg))] : [b]; if (blocks.length > 1) joinBlocks(blocks); // (owner, Sep 22) a design's wings: annexes on the lot, built and standing with the main block, a door between the blocks where they meet
        for (const blk of blocks) { if (progress >= 1) { nearBuildings.push(blk); if (here) c.buildings.push(blk); } else if (here) c.sites.push(blk); }
        if (progress <= 0 || !here) continue;
      } else if (b) { nearBuildings.push(b); if (here) c.buildings.push(b); }
      if (!here) continue;
      const gt = this.fields.zone(lot.zone).groundTerrain;
      const gi = gt !== undefined ? this.terrainIndex.get(gt) : undefined;
      forRect(c, lot.x, lot.y, lot.w, lot.h, (i) => { inLot[i] = gi !== undefined ? gi + 1 : 255; });
    }

    for (let ly = 0; ly < CHUNK; ly++) {
      for (let lx = 0; lx < CHUNK; lx++) {
        const x = x0 + lx, y = y0 + ly, i = ly * CHUNK + lx;
        let t = this.fields.terrainAt(x, y);
        const place = places.length ? this.places.at(x, y) : undefined;
        if (place?.terrain) { const pt = this.reg.get('terrain', place.terrain); if (pt) t = pt; }
        if (place?.paint) for (const r of place.paint) if (x >= place.x + r.x && x < place.x + r.x + r.w && y >= place.y + r.y && y < place.y + r.y + r.h) { const pt = this.reg.get('terrain', r.terrain); if (pt) t = pt; }
        let ti = this.terrainIndex.get(t.id)!, causeway = false;
        const water = !t.walkable && !t.buildable;
        const planned = ov.zones.size ? ov.zoneAt(x, y) : undefined;
        c.zone[i] = water ? ZONE_INDEX.water : ZONE_INDEX[place?.zone ?? planned ?? this.fields.urbanZone(x, y)];

        // roads: level-0 lines bridge water, finer lines stop at the shore
        this.net.hitsAt(x, y, hits);
        let flags = 0, primary: RoadHit | undefined;
        const roadMode = place?.roads ?? 'keep';
        for (const h of hits) {
          if (!this.roadKept(roadMode, h)) continue;
          // a highway on an overpass leaves the ground layer: its tiles go to the deck arrays
          const dh = this.net.overpassH(h, x, y);
          if (dh > 0) {
            const onDeck = RoadNet.covers(0, h.road.width, h.off);
            let df = h.axis === 'v' ? RF_V | (h.off === 0 ? RF_VC : 0) | ((h.road.width & 1) === 0 ? RF_VEVEN : 0) : RF_H | (h.off === 0 ? RF_HC : 0) | ((h.road.width & 1) === 0 ? RF_HEVEN : 0);
            if (!onDeck) df |= RF_SIDEWALK;
            if (h.ledge) df |= RF_LEDGE;
            c.deckH[i] = dh; c.deckRoad[i] = this.roadIndex.get(h.road.id)! + 1; c.deckFlags[i] = df;
            // the embankment under a ramp is solid for whatever is on the ground; the flat deck has headroom
            if (dh < OVER_H) c.blockH[i] = Math.max(c.blockH[i], dh);
            continue;
          }
          if (h.level > 0 && h.road.class !== 'rail') {
            // finer roads stop at the shore: the line ends cleanly across its whole width (decided at the centre), and
            // no single tile of it is laid on water — along a diagonal shore the width alone used to reach out over the
            // sea, a grey wedge with a barrier on it, and a boat or a player on those tiles counted as "under a bridge"
            // (owner, Sep 8, with a drawing of where the edge belongs).
            // A railway is the exception: it bridges water like a level-0 line, because a train cannot go round
            // (owner, Sep 8: the rails stopped at the shore and the train carried on into the harbour).
            // (bug report, Sep 12: "car sinks through the road") on the path network the road is painted from the edge's
            // curve, so a stretch the deck profile crosses at grade (the water too close to a node for a ramp: deckRows)
            // has to *be* road under the paint — a causeway on a gravel bank — or the car drives into the lake
            if (water && this.paths) { causeway = true; }
            else {
              if (water) continue;
              const ct = h.axis === 'v' ? this.fields.terrainAt(h.pos, y) : this.fields.terrainAt(x, h.pos);
              if (!ct.walkable && !ct.buildable) continue;
            }
          }
          const onRoad = RoadNet.covers(0, h.road.width, h.off);
          if (h.axis === 'v') { flags |= RF_V; if (h.off === 0) flags |= RF_VC; if ((h.road.width & 1) === 0) flags |= RF_VEVEN; }
          else { flags |= RF_H; if (h.off === 0) flags |= RF_HC; if ((h.road.width & 1) === 0) flags |= RF_HEVEN; }
          if (!onRoad) flags |= RF_SIDEWALK;
          if (!primary || (!RoadNet.covers(0, primary.road.width, primary.off) && onRoad)) primary = h;
        }
        // interchange slip lanes beside an overpass (a plain street on the ground layer)
        const fr = (!primary || (flags & RF_SIDEWALK)) && roadMode === 'keep' && !c.deckH[i] ? this.net.frontageAt(x, y) : null;
        if (fr) { primary = { road: this.frontageRoad ?? fr.road, axis: fr.axis, level: 0, k: 0, pos: 0, off: 0 }; flags = (fr.axis === 'v' ? RF_V : RF_H) | (flags & ~RF_SIDEWALK); }
        if (primary) {
          if (causeway && water) { const gi = this.terrainIndex.get('gravel'); if (gi !== undefined) ti = gi; c.zone[i] = ZONE_INDEX.rural; } // (a bank under the road: land for the physics, the shore for the painter)
          c.road[i] = this.roadIndex.get(primary.road.id)! + 1;
          if (RoadNet.covers(0, primary.road.width, primary.off)) flags &= ~RF_SIDEWALK;
          c.roadFlags[i] = flags;
        } else if (inLot[i] && t.buildable) {
          if (inLot[i] !== 255) ti = inLot[i] - 1;
        } else if (place?.terrain || place?.paint) {
          // authored ground: no decor
        } else if (!water && t.buildable && !planned && this.zoneGround[c.zone[i]] !== undefined) {
          ti = this.zoneGround[c.zone[i]]!; // dense zones: whole block interiors are paved
        // (owner, Sep 14: "trees should not be near roads, give it at least a car's width") nothing is planted within 2.2 tiles of the
        // road's painted band, sidewalks included: a tree right at the kerb caught the wing of every car that drifted to the edge
        } else if (!water && t.decor && t.decor.length && !c.deckH[i] && !this.underStroke(x, y, 2.2) && !this.nearRoad(c, lx, ly)) {
          // nothing planted under a deck (a tree through a bridge, a bush on the embankment: owner's night reports, Sep 10), nor under the painted road
          for (let d = 0; d < t.decor.length; d++) {
            const dd = t.decor[d];
            if (hash01(this.seed + 100 + d, x, y) < dd.density) {
              c.decor.push({ x, y, terrain: ti, index: d });
              if (dd.height) { c.blockH[i] = Math.max(c.blockH[i], dd.height); c.decorH[i] = dd.height; }
              break;
            }
          }
        }
        c.terrain[i] = ti;
      }
    }

    for (const b of c.buildings) {
      if (b.def.ground) {
        const gi = this.terrainIndex.get(b.def.ground);
        if (gi !== undefined) forRect(c, b.x, b.y, b.w, b.h, (i) => { c.terrain[i] = gi; if (c.zone[i] === ZONE_INDEX.water) c.zone[i] = ZONE_INDEX.rural; });
      }
      if (!(b.def.solid ?? true) || b.height <= 0) continue;
      forRect(c, b.x, b.y, b.w, b.h, (i) => { c.blockH[i] = Math.max(c.blockH[i], b.height); });
    }
    // (owner, Sep 11: "the car can go under the concrete foot of the bridge") the embankment beside a ramp is solid ground:
    // the renderer draws a bank `BANK` times the ramp's height wide either side of a raised road (decksStroked's skirt),
    // and its tiles now carry that slope as blockH, falling from the ramp's height at the deck's edge to nothing at the
    // toe — so a car cannot pass through it from the ground and whatever leaves the ramp sideways lands on the bank.
    // Never over a road, a deck or a building; the flat deck stands on pillars and gets no bank.
    if (this.paths) {
      const n = CHUNK * CHUNK, ramp: { lx: number; ly: number; h: number; across: boolean }[] = [];
      for (let i = 0; i < n; i++) { const h = c.deckH[i]; if (h > 0 && h < OVER_H - 0.3) ramp.push({ lx: i % CHUNK, ly: Math.floor(i / CHUNK), h, across: (c.deckFlags[i] & RF_V) !== 0 }); }
      // (the overnight review, Sep 12) a ramp in the next chunk banks into this one too: its deck tiles within a bank's
      // width outside the border seed as well, from the network, since the neighbour's arrays are not here
      const apron = Math.ceil((OVER_H - 0.3) * BANK), ah: RoadHit[] = [];
      for (let ly = -apron; ly < CHUNK + apron; ly++) for (let lx = -apron; lx < CHUNK + apron; lx++) {
        if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) { if (lx === 0) lx = CHUNK - 1; continue; }
        const x = c.x0 + lx, y = c.y0 + ly;
        for (const h of this.net.hitsAt(x, y, ah)) { const dh = this.net.overpassH(h, x, y); if (dh > 0) { if (dh < OVER_H - 0.3) ramp.push({ lx, ly, h: dh, across: h.axis === 'v' }); break; } }
      }
      for (const { lx, ly, h, across } of ramp) {
        const w = h * BANK;
        for (const dir of [-1, 1]) for (let k = 1; k <= Math.ceil(w); k++) {
          const tx = across ? lx + dir * k : lx, ty = across ? ly : ly + dir * k;
          const inside = tx >= 0 && ty >= 0 && tx < CHUNK && ty < CHUNK;
          if (!inside) { if ((across ? tx : ty) < -apron || (across ? tx : ty) >= CHUNK + apron) break; continue; } // (walk on: the bank may re-enter)
          const j = ty * CHUNK + tx;
          if (c.deckH[j] > 0) break; // (the ramp itself, or its neighbour's)
          if (c.road[j] || c.blockH[j] >= h) break; // (a road, a building: the bank stops at it)
          const bh = h * (1 - (k - 0.5) / w); if (bh <= BANK_TOE) break; // (bug report, Sep 22: "car hits a ghost wall driving west to this bridge" — the toe beside a ramp's foot was 10–50 cm of solid bank, and a car in the outer lane caught it with a corner: a bank lower than a kerb a car mounts is not a wall)
          if (bh > c.blockH[j]) c.blockH[j] = bh;
        }
      }
    }
    // parking lots beside businesses: paved, cleared of decor, no props; only where the whole pad is free ground
    const pavement = this.terrainIndex.get('pavement');
    const noProp = new Set<number>();
    const padFree = (b: PlacedBuilding, dw: Driveway): boolean => {
      for (let ty = dw.y; ty < dw.y + dw.h; ty++) for (let tx = dw.x; tx < dw.x + dw.w; tx++) {
        const t = this.fields.terrainAt(tx, ty);
        if (!t.buildable) return false;
        if (this.net.hitsAt(tx, ty, hits).some((h) => RoadNet.covers(0, h.road.width, h.off) && !(h.level > 0 && !this.shoreOk(h, tx, ty)))) return false; // the carriageway (a sidewalk is the kerb cut)
        if (nearBuildings.some((o) => o !== b && tx >= o.x && tx < o.x + o.w && ty >= o.y && ty < o.y + o.h)) return false;
        if (places.some((p) => (p.lots ?? 'none') === 'none' && tx >= p.x && tx < p.x + p.w && ty >= p.y && ty < p.y + p.h)) return false;
      }
      return true;
    };
    // (bug report, Sep 21: "there's two parking lots in the same place") two businesses side by side put their lots on the same ground: the
    // pads are laid in the order of their buildings' keys and a pad over one already laid takes its other side, or none — the same order
    // in every chunk that sees both
    const laid: Driveway[] = [];
    const overLaid = (dw: Driveway) => laid.some((o) => dw.x < o.x + o.w && o.x < dw.x + dw.w && dw.y < o.y + o.h && o.y < dw.y + dw.h);
    const byKey = nearBuildings.filter(isBusiness).sort((a, b2) => a.x - b2.x || a.y - b2.y);
    for (const b of byKey) {
      // beside the building on the side away from the door; if that side is taken, the other side
      let dw = driveway(b, this.seed);
      if (!padFree(b, dw) || overLaid(dw)) { dw = driveway(b, this.seed, true); if (!padFree(b, dw) || overLaid(dw)) continue; }
      laid.push(dw);
      if (dw.x + dw.w <= x0 - PAD || dw.x >= x0 + CHUNK + PAD || dw.y + dw.h <= y0 - PAD || dw.y >= y0 + CHUNK + PAD) continue;
      if (dw.x < x0 + CHUNK && dw.x + dw.w > x0 && dw.y < y0 + CHUNK && dw.y + dw.h > y0) c.parking.push({ ...dw, key: `${b.x},${b.y}` });
      forRect(c, dw.x, dw.y, dw.w, dw.h, (i) => { if (!c.road[i] && pavement !== undefined) c.terrain[i] = pavement; });
      // the pad and the kerb cut in front of it (two tiles towards the street): no trees, no street furniture
      const [nx, ny] = [Math.round(Math.sin(dw.angle)), Math.round(-Math.cos(dw.angle))];
      const ex = dw.x + (nx < 0 ? -2 : 0), ey = dw.y + (ny < 0 ? -2 : 0), ew = dw.w + (nx ? 2 : 0), eh = dw.h + (ny ? 2 : 0);
      forRect(c, ex, ey, ew, eh, (i) => { if (c.decorH[i] > 0 && c.blockH[i] <= c.decorH[i] + 1e-6) { c.blockH[i] = 0; c.decorH[i] = 0; } noProp.add(i); });
      c.decor = c.decor.filter((d) => d.x < ex || d.x >= ex + ew || d.y < ey || d.y >= ey + eh);
    }
    // props: street furniture on sidewalks / lots / parks / rural roadsides — never on a doorstep
    if (this.propList.length) {
      const doorsteps = new Set<number>();
      for (const b of nearBuildings) { // (Sep 29: the buildings next door too — a door on a chunk's edge has its step in the next chunk)
        if (b.fe) for (let ty = b.fe.y; ty < b.fe.y + b.fe.h; ty++) for (let tx = b.fe.x; tx < b.fe.x + b.fe.w; tx++) { const lx = tx - x0, ly = ty - y0; if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) doorsteps.add(ly * CHUNK + lx); } // (Sep 29) under a fire escape: no tree, no bench
        const plan = floorPlan(b, 0, this.seed); if (!plan) continue;
        for (const [dx, dy] of plan.doors) for (let k = 1; k <= 2; k++) {
          const sx = b.rot === 1 ? dx - k : b.rot === 3 ? dx + k : dx, sy = b.rot === 0 ? dy + k : b.rot === 2 ? dy - k : dy;
          const lx = sx - x0, ly = sy - y0;
          if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) doorsteps.add(ly * CHUNK + lx);
        }
      }
      // (owner, Sep 29: "walking through doors is hard sometimes … stuck before the door") a tree could still grow on a doorstep: the
      // police precinct at -82,-13 had one on the tile in front of its door. The doorsteps are clear of trees as of props
      if (doorsteps.size) { for (const i of doorsteps) if (c.decorH[i] > 0 && c.blockH[i] <= c.decorH[i] + 1e-6) { c.blockH[i] = 0; c.decorH[i] = 0; } c.decor = c.decor.filter((d) => !doorsteps.has((d.y - y0) * CHUNK + (d.x - x0))); }
      for (let ly = 0; ly < CHUNK; ly++) for (let lx = 0; lx < CHUNK; lx++) {
        const i = ly * CHUNK + lx;
        if (c.blockH[i] > 0 || c.deckH[i] > 0 || c.zone[i] === ZONE_INDEX.water || doorsteps.has(i) || noProp.has(i)) continue;
        const x = x0 + lx, y = y0 + ly;
        let placement: Prop['placement'] | undefined;
        if (c.road[i]) { if (c.roadFlags[i] & RF_SIDEWALK) placement = 'sidewalk'; }
        else if (inLot[i]) placement = 'lot';
        else if (c.zone[i] === ZONE_INDEX.park) placement = 'park';
        else if (c.zone[i] === ZONE_INDEX.rural && this.nearRoad(c, lx, ly)) placement = 'roadside';
        if (!placement) continue;
        // (path world) the picture is the stroke, not the tiles: a lamp goes on the painted pavement, never on the
        // painted carriageway (owner, Sep 10: "the streetlight is on the road and its hitbox is bigger than it displays as")
        if (this.paths && placement !== 'lot') { const so = (this.net as PathRoads).strokeOff(x, y, 1); if (placement === 'sidewalk' ? !so || so.off <= so.width / 2 + 0.5 || so.off > so.total / 2 + 0.5 : so && so.off <= so.total / 2 + 0.5) continue; }
        const cands = this.propsByPlacement[placement];
        if (!cands?.length) continue;
        const zone = ZONES[c.zone[i]];
        const r = hash01(this.seed + 300, x, y);
        // total density of applicable props decides whether anything is here
        let total = 0;
        for (const cnd of cands) total += cnd.prop.density * (cnd.prop.zones[zone] ?? 0);
        if (total <= 0 || r >= Math.min(0.9, total)) continue;
        let pick = hash01(this.seed + 301, x, y) * total;
        let chosen = cands[cands.length - 1];
        for (const cnd of cands) { pick -= cnd.prop.density * (cnd.prop.zones[zone] ?? 0); if (pick < 0) { chosen = cnd; break; } }
        if (chosen.prop.density * (chosen.prop.zones[zone] ?? 0) <= 0) continue;
        // (owner, Sep 14: "trees should not be near roads, give it at least a car's width") a tall prop — a tree in a garden, a
        // roadside tree — stands 2.2 tiles clear of the road's painted band whatever placement it came by; the lot's own front strip
        // included, where a garden tree right at the pavement caught the wing of every car that drifted to the edge
        if (this.paths && chosen.prop.height > 2.5) { const so = (this.net as PathRoads).strokeOff(x, y, 2.2); if (so && so.off <= so.total / 2 + 2.2) continue; }
        c.props.push({ x, y, prop: chosen.index });
        if (chosen.prop.height > 0) { c.blockH[i] = Math.max(c.blockH[i], chosen.prop.height); c.decorH[i] = Math.max(c.decorH[i], chosen.prop.height); }
      }
      // (owner, Sep 25: Area 69's "ufo props") a place's own props, set down where it says over whatever the tile grew — never in a building
      for (const pl of places) for (const pp of pl.props ?? []) {
        const x = pl.x + pp.x, y = pl.y + pp.y, lx = x - x0, ly = y - y0, pi = this.propList.findIndex((q) => q.id === pp.id);
        if (pi < 0 || lx < 0 || ly < 0 || lx >= CHUNK || ly >= CHUNK || c.blockH[ly * CHUNK + lx] > c.decorH[ly * CHUNK + lx]) continue;
        const i = ly * CHUNK + lx, h = this.propList[pi].height;
        c.props = c.props.filter((q) => q.x !== x || q.y !== y); c.decor = c.decor.filter((d) => d.x !== x || d.y !== y);
        c.props.push({ x, y, prop: pi }); c.blockH[i] = h; c.decorH[i] = h;
      }
      if (this.paths) this.pathFurniture(c, x0, y0, doorsteps, noProp); // (Sep 23: the path world's lamps and cameras)
      if (this.paths) this.pathBoards(c, x0, y0); // (Sep 25: the big billboards beside the motorways and avenues)
      this.roofBoards(c);
    }
    // a launch ramp on the shore (owner, Q65b: "so the shore is obvious"): one per chunk with real water — a land tile with water beside it, a road within four tiles (a marina or boathouse nearby wins the spot)
    { const ramps = this.propsByPlacement.shore ?? [];
      let water = 0; for (let i = 0; i < c.zone.length; i += 4) if (c.zone[i] === ZONE_INDEX.water) water++;
      if (ramps.length && water * 4 >= 200) {
        const boaty = c.buildings.filter((b) => b.def.tags.some((t) => t === 'marina' || t === 'boathouse' || t === 'boats'));
        let best = -1, bs = Infinity;
        for (let ly = 1; ly < CHUNK - 1; ly++) for (let lx = 1; lx < CHUNK - 1; lx++) {
          const i = ly * CHUNK + lx; if (c.zone[i] === ZONE_INDEX.water || c.road[i] || c.blockH[i] > 0) continue;
          const wet = c.zone[i - 1] === ZONE_INDEX.water || c.zone[i + 1] === ZONE_INDEX.water || c.zone[i - CHUNK] === ZONE_INDEX.water || c.zone[i + CHUNK] === ZONE_INDEX.water;
          if (!wet) continue;
          let road = false; for (let dy = -4; dy <= 4 && !road; dy++) for (let dx = -4; dx <= 4 && !road; dx++) { const nx = lx + dx, ny = ly + dy; if (nx >= 0 && ny >= 0 && nx < CHUNK && ny < CHUNK && c.road[ny * CHUNK + nx] && !(c.roadFlags[ny * CHUNK + nx] & RF_SIDEWALK)) road = true; }
          if (!road) continue;
          const x = x0 + lx, y = y0 + ly;
          let score = hash01(this.seed + 303, x, y); for (const b of boaty) score = Math.min(score, Math.hypot(x - b.x - b.w / 2, y - b.y - b.h / 2) / 40); // (nearer a marina: a better spot)
          if (score < bs) { bs = score; best = i; }
        }
        if (best >= 0) { const rp = ramps[hash(this.seed, c.cx, c.cy, 44) % ramps.length]; const x = x0 + (best % CHUNK), y = y0 + Math.floor(best / CHUNK); c.props = c.props.filter((q) => q.x !== x || q.y !== y); c.decor = c.decor.filter((d) => d.x !== x || d.y !== y); c.props.push({ x, y, prop: rp.index }); }
      } }
    // a terminus (owner, Q66): the last PLATFORM_LEN tiles of a railway's dead end get a platform strip on both sides of the ballast and a canopy
    // in the middle of each — decided per tile (a platform can straddle a chunk edge): a tile two off a rail's centre line whose track ends within reach
    { const plats = this.propsByPlacement.terminus ?? [];
      const slab = plats.find((q) => q.prop.height === 0), roof = plats.find((q) => q.prop.height > 0);
      const railIdx = this.roadList.findIndex((r) => r.class === 'rail') + 1;
      const railCentreAt = (lx: number, ly: number): boolean => { if (lx < 0 || ly < 0 || lx >= CHUNK || ly >= CHUNK) return true; const j = ly * CHUNK + lx; return c.road[j] === railIdx && (c.roadFlags[j] & (RF_VC | RF_HC)) !== 0; }; // (outside the chunk: maybe — the slow check decides)
      if (slab && railIdx > 0) for (let ly = 0; ly < CHUNK; ly++) for (let lx = 0; lx < CHUNK; lx++) {
        const i = ly * CHUNK + lx; if (c.road[i] || c.zone[i] === ZONE_INDEX.water || c.blockH[i] > 0) continue;
        if (!railCentreAt(lx + 2, ly) && !railCentreAt(lx - 2, ly) && !railCentreAt(lx, ly + 2) && !railCentreAt(lx, ly - 2)) continue;
        const tx = x0 + lx, ty = y0 + ly;
        if (this.net.hitsAt(tx, ty, hits).length) continue; // (a road's own tiles, a crossing's: no platform)
        const end = railEndNear(this.net, tx, ty); if (end === undefined) continue;
        c.props = c.props.filter((q) => q.x !== tx || q.y !== ty); c.decor = c.decor.filter((d) => d.x !== tx || d.y !== ty);
        c.props.push({ x: tx, y: ty, prop: (roof && end === Math.floor(PLATFORM_LEN / 2) ? roof : slab).index });
        if (roof && end === Math.floor(PLATFORM_LEN / 2)) { c.blockH[i] = Math.max(c.blockH[i], roof.prop.height); c.decorH[i] = Math.max(c.decorH[i], roof.prop.height); }
      } }
    // what a parks crew planted: a prop on a free tile (replacing whatever decor the terrain had there)
    if (ov.plants.size) for (const pl of ov.plantsIn(x0, y0, x0 + CHUNK, y0 + CHUNK)) {
      const i = (pl.y - y0) * CHUNK + (pl.x - x0);
      const pi = this.propList.findIndex((q) => q.id === pl.p);
      if (pi < 0 || c.road[i] || (c.blockH[i] > 0 && c.decorH[i] <= 0)) continue;
      c.decor = c.decor.filter((d) => d.x !== pl.x || d.y !== pl.y);
      c.props = c.props.filter((q) => q.x !== pl.x || q.y !== pl.y);
      c.props.push({ x: pl.x, y: pl.y, prop: pi });
      const h = this.propList[pi].height;
      c.blockH[i] = h; c.decorH[i] = h;
    }
    // sort for painter's algorithm: lower y first, then x
    c.buildings.sort((a, b) => a.y + a.h - (b.y + b.h) || a.x - b.x);
    this.pads(c);
    return c;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function forRect(c: Chunk, x: number, y: number, w: number, h: number, f: (i: number) => void): void {
  const lx0 = Math.max(0, x - c.x0), ly0 = Math.max(0, y - c.y0);
  const lx1 = Math.min(CHUNK, x + w - c.x0), ly1 = Math.min(CHUNK, y + h - c.y0);
  for (let ly = ly0; ly < ly1; ly++) for (let lx = lx0; lx < lx1; lx++) f(ly * CHUNK + lx);
}
